/**
 * Kiosk JWT Token Service
 *
 * Handles requesting and caching a kiosk JWT token from the backend.
 *
 * Flow:
 *   1. index.js requests the token on device load → cached in localStorage
 *   2. index.js forwards token to kiosk-page.html via ?kiosk_token= URL param
 *   3. kiosk-page.js reads the URL param, saves to localStorage, uses it
 *   4. On subsequent exit→re-enter cycles the cached token is used directly
 *   5. Token is sent as jwt_token field in photo list requests
 */

const STORAGE_KEY = 'occo_kiosk_jwt_token';
// Minimum refresh buffer: don't refresh if more than 5 minutes remain.
// For short-lived tokens (< 10 minutes), don't trigger proactive refresh at all.
const MIN_REFRESH_BUFFER_MS = 5 * 60 * 1000;
const DEFAULT_ACCESS_TOKEN_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const DEFAULT_REFRESH_TOKEN_TTL_MS = 30 * 24 * 60 * 60 * 1000;

let refreshInFlight: Promise<string> | null = null;

interface CachedToken {
  token?: string;
  accessToken: string;
  refreshToken?: string;
  /** Unix timestamp in milliseconds */
  issuedAt?: number;
  /** Unix timestamp in milliseconds */
  expiresAt: number;
  /** Unix timestamp in milliseconds */
  refreshExpiresAt?: number;
  /** pvId the token was issued for */
  pvId: string;
  /** kioskId the token was issued for */
  kioskId: string;
}

interface TokenResponseResult {
  token?: string;
  accessToken?: string;
  refreshToken?: string;
  kioskId?: string;
  eventId?: number;
  eventName?: string;
  tokenVersion?: number;
  createdAt?: string;
  expiresAt?: string;
  expiresInSeconds?: number;
  refreshExpiresAt?: string;
  refreshExpiresInSeconds?: number;
  tokenType?: string;
}

/** Backend wraps its response in { status, result } */
interface TokenResponse {
  /** Unwrapped flat token (legacy / future format) */
  token?: string;
  accessToken?: string;
  refreshToken?: string;
  createdAt?: string;
  expiresAt?: string;
  expiresInSeconds?: number;
  refreshExpiresAt?: string;
  refreshExpiresInSeconds?: number;
  /** Wrapped format: { status: "ok", result: { token, ... } } */
  status?: string;
  error_message_key?: string;
  error_message?: string;
  error?: { message_text?: string; code?: string; message_key?: string };
  result?: TokenResponseResult;
}

interface SaveKioskTokenParams {
  token: string;
  pvId: string;
  kioskId: string;
  expiresAt?: number;
  refreshToken?: string;
  refreshExpiresAt?: number;
}

interface ParsedKioskTokenPayload {
  accessToken: string;
  refreshToken?: string;
  issuedAt: number;
  expiresAt: number;
  refreshExpiresAt?: number;
}

function parseTimestamp(value?: string | number): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (!value) {
    return null;
  }

  const parsed = new Date(value).getTime();

  return Number.isFinite(parsed) ? parsed : null;
}

function buildKioskTokenError(message: string, code?: string): Error {
  const error = new Error(message);

  if (code) {
    (error as Error & { code?: string }).code = code;
  }

  return error;
}

function getResponsePayload(data: TokenResponse): TokenResponseResult | TokenResponse {
  if (data.status === 'nok') {
    const code = data.error_message_key ?? data.error?.code ?? data.error?.message_key;
    const message =
      data.error?.message_text ?? data.error_message ?? data.error_message_key ?? 'kiosk_token_request_failed';

    throw buildKioskTokenError(`[kioskTokenService] ${message}`, code);
  }

  return data.result ?? data;
}

function resolveExpiration(
  isoDate?: string,
  expiresInSeconds?: number,
  fallbackMs: number = DEFAULT_ACCESS_TOKEN_TTL_MS
): number {
  const parsedDate = parseTimestamp(isoDate);

  if (parsedDate !== null) {
    return parsedDate;
  }

  if (expiresInSeconds) {
    return Date.now() + expiresInSeconds * 1000;
  }

  return Date.now() + fallbackMs;
}

function parseKioskTokenResponse(
  data: TokenResponse,
  options: { requireRefreshToken?: boolean } = {}
): ParsedKioskTokenPayload {
  const payload = getResponsePayload(data);
  const accessToken = payload.accessToken ?? payload.token;
  const { refreshToken } = payload;

  if (!accessToken) {
    throw buildKioskTokenError('[kioskTokenService] Backend response missing access token field');
  }

  if (options.requireRefreshToken && !refreshToken) {
    throw buildKioskTokenError(
      '[kioskTokenService] Backend response missing refresh token field',
      'refresh_token_required'
    );
  }

  return {
    accessToken,
    refreshToken,
    issuedAt: parseTimestamp(payload.createdAt) ?? Date.now(),
    expiresAt: resolveExpiration(payload.expiresAt, payload.expiresInSeconds, DEFAULT_ACCESS_TOKEN_TTL_MS),
    refreshExpiresAt: payload.refreshToken
      ? resolveExpiration(payload.refreshExpiresAt, payload.refreshExpiresInSeconds, DEFAULT_REFRESH_TOKEN_TTL_MS)
      : undefined,
  };
}

function toCachedToken(raw: CachedToken | null): CachedToken | null {
  if (!raw) {
    return null;
  }

  const accessToken = raw.accessToken ?? raw.token;

  if (!accessToken) {
    return null;
  }

  const expiresAt = parseTimestamp(raw.expiresAt);

  if (expiresAt === null) {
    return null;
  }

  const refreshExpiresAt = parseTimestamp(raw.refreshExpiresAt);
  const issuedAt = parseTimestamp(raw.issuedAt);

  return {
    ...raw,
    accessToken,
    token: raw.token ?? accessToken,
    expiresAt,
    refreshExpiresAt: refreshExpiresAt ?? undefined,
    issuedAt: issuedAt ?? undefined,
  };
}

function canReuseStoredSession(cached: CachedToken | null, pvId: string, kioskId: string): cached is CachedToken {
  return cached?.pvId === pvId && cached?.kioskId === String(kioskId);
}

function isRefreshTokenUsable(cached: CachedToken | null): cached is CachedToken {
  if (!cached?.refreshToken || !cached.refreshExpiresAt) {
    return false;
  }

  return cached.refreshExpiresAt > Date.now();
}

/** Private: write token data to localStorage. */
function saveTokenToStorage(data: CachedToken): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...data,
        token: data.accessToken,
      })
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[kioskTokenService] Failed to persist token to localStorage:', e);
  }
}

/** Private: read cached token from localStorage. */
function loadTokenFromStorage(): CachedToken | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? toCachedToken(JSON.parse(raw) as CachedToken) : null;
  } catch {
    return null;
  }
}

/** Private: check whether a cached token is still fresh enough to use. */
function isCachedTokenValid(cached: CachedToken | null, pvId: string, kioskId: string): cached is CachedToken {
  if (!cached?.accessToken || !cached.expiresAt) return false;
  if (!canReuseStoredSession(cached, pvId, kioskId)) return false;

  const remainingMs = cached.expiresAt - Date.now();
  if (remainingMs <= 0) return false;

  const issuedAt = typeof cached.issuedAt === 'number' ? cached.issuedAt : null;
  const hasKnownLifetime = issuedAt !== null && issuedAt > 0 && cached.expiresAt > issuedAt;

  // Legacy cache entries (no issuedAt) and short-lived tokens skip proactive refresh.
  // They are reused until expiry; reactive refresh handles true expiration.
  if (!hasKnownLifetime) return true;

  const tokenLifetimeMs = cached.expiresAt - issuedAt;

  if (tokenLifetimeMs <= MIN_REFRESH_BUFFER_MS * 2) return true;

  // Only long-lived tokens use proactive refresh when under the 5-minute buffer.
  return remainingMs > MIN_REFRESH_BUFFER_MS;
}

function persistIssuedToken(issuedToken: ParsedKioskTokenPayload, pvId: string, kioskId: string): string {
  saveTokenToStorage({
    accessToken: issuedToken.accessToken,
    token: issuedToken.accessToken,
    refreshToken: issuedToken.refreshToken,
    issuedAt: issuedToken.issuedAt,
    expiresAt: issuedToken.expiresAt,
    refreshExpiresAt: issuedToken.refreshExpiresAt,
    pvId,
    kioskId: String(kioskId),
  });

  return issuedToken.accessToken;
}

function getApiBase(backendApiBase: string): string {
  return (backendApiBase ?? '').replace(/\/$/, '');
}

async function postKioskAuthRequest(
  path: string,
  body: Record<string, string>,
  backendApiBase: string
): Promise<TokenResponse> {
  const response = await fetch(`${getApiBase(backendApiBase)}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const responseText = await response.text().catch(() => '');
  let data: TokenResponse = {};

  if (responseText) {
    try {
      data = JSON.parse(responseText) as TokenResponse;
    } catch {
      data = {};
    }
  }

  if (!response.ok) {
    const code = data.error_message_key ?? data.error?.code ?? data.error?.message_key;
    const message =
      data.error?.message_text ?? data.error_message ?? (responseText || `Request failed (${response.status})`);

    throw buildKioskTokenError(`[kioskTokenService] ${message}`, code);
  }

  return data;
}

/**
 * Public: persist a token received externally (e.g. via ?kiosk_token= URL
 * param forwarded from index.js) so future loads can use the localStorage
 * cache path without a network round-trip.
 */
export function saveKioskToken({ token, pvId, kioskId, expiresAt }: SaveKioskTokenParams): void {
  const resolvedExpiresAt = expiresAt ?? Date.now() + 30 * 24 * 60 * 60 * 1000;
  const cached = loadTokenFromStorage();
  const shouldPreserveRefreshSession = canReuseStoredSession(cached, pvId, kioskId);

  saveTokenToStorage({
    accessToken: token,
    token,
    issuedAt: Date.now(),
    refreshToken: shouldPreserveRefreshSession ? cached.refreshToken : undefined,
    refreshExpiresAt: shouldPreserveRefreshSession ? cached.refreshExpiresAt : undefined,
    pvId,
    kioskId: String(kioskId),
    expiresAt: resolvedExpiresAt,
  });
}

/** Private: fetch a new kiosk JWT token from the backend. */
async function fetchNewKioskToken(backendApiBase: string, pvId: string, kioskId: string): Promise<string> {
  const data = await postKioskAuthRequest('/auth/kiosk/token', { pvId, kioskId: String(kioskId) }, backendApiBase);
  const issuedToken = parseKioskTokenResponse(data);

  return persistIssuedToken(issuedToken, pvId, kioskId);
}

async function refreshKioskTokenPair(backendApiBase: string, cached: CachedToken): Promise<string> {
  const data = await postKioskAuthRequest(
    '/auth/kiosk/token/refresh',
    { refreshToken: cached.refreshToken ?? '' },
    backendApiBase
  );
  const refreshedToken = parseKioskTokenResponse(data);

  return persistIssuedToken(
    {
      ...refreshedToken,
      refreshToken: cached.refreshToken,
      refreshExpiresAt: cached.refreshExpiresAt,
    },
    cached.pvId,
    cached.kioskId
  );
}

export function clearKioskToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[kioskTokenService] Failed to clear token from localStorage:', e);
  }
}

export async function ensureKioskTokenSession(backendApiBase: string, pvId: string, kioskId: string): Promise<string> {
  if (!pvId) throw new Error('[kioskTokenService] pvId is required');
  if (!kioskId) throw new Error('[kioskTokenService] kioskId is required');

  const cached = loadTokenFromStorage();

  if (canReuseStoredSession(cached, pvId, kioskId) && isRefreshTokenUsable(cached)) {
    if (isCachedTokenValid(cached, pvId, kioskId)) {
      return cached.accessToken;
    }

    return refreshKioskAccessToken(backendApiBase);
  }

  return fetchNewKioskToken(backendApiBase, pvId, kioskId);
}

export async function reauthenticateKioskToken(backendApiBase: string): Promise<string> {
  const cached = loadTokenFromStorage();

  if (!cached?.pvId || !cached.kioskId) {
    throw buildKioskTokenError(
      '[kioskTokenService] Cannot re-authenticate kiosk token without pvId and kioskId',
      'invalid_private_key'
    );
  }

  clearKioskToken();

  return fetchNewKioskToken(backendApiBase, cached.pvId, cached.kioskId);
}

export async function refreshKioskAccessToken(backendApiBase: string): Promise<string> {
  refreshInFlight ??= (async () => {
    const cached = loadTokenFromStorage();

    if (!cached) {
      throw buildKioskTokenError('[kioskTokenService] kiosk token cache missing', 'refresh_token_required');
    }

    if (!isRefreshTokenUsable(cached)) {
      return reauthenticateKioskToken(backendApiBase);
    }

    try {
      return await refreshKioskTokenPair(backendApiBase, cached);
    } catch (error) {
      clearKioskToken();

      if (error instanceof Error) {
        (error as Error & { kioskReauthAttempted?: boolean }).kioskReauthAttempted = true;
      }

      return fetchNewKioskToken(backendApiBase, cached.pvId, cached.kioskId);
    }
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

/**
 * Public: get a valid kiosk JWT token — uses localStorage cache when
 * possible, fetches from backend on cache miss.
 *
 * @param backendApiBase  – e.g. "https://api.occo.io/v1"  (window.BACKEND_API / process.env.BACKEND_API)
 * @param pvId            – event private API key
 * @param kioskId         – kiosk device identifier
 */
export async function requestKioskToken(backendApiBase: string, pvId: string, kioskId: string): Promise<string> {
  if (!pvId) throw new Error('[kioskTokenService] pvId is required');
  if (!kioskId) throw new Error('[kioskTokenService] kioskId is required');

  const cached = loadTokenFromStorage();

  if (isCachedTokenValid(cached, pvId, kioskId)) {
    return cached.accessToken;
  }

  if (canReuseStoredSession(cached, pvId, kioskId) && isRefreshTokenUsable(cached)) {
    return refreshKioskAccessToken(backendApiBase);
  }

  return fetchNewKioskToken(backendApiBase, pvId, kioskId);
}
